<template>
  <DialogTemplate
    :show-controls="true"
    class="dialog"
    position="top-right"
    margin="small"
  >
    <template #header>
      <icon
        v-if="!fromUserDialog && paths.length === 1"
        name="settings"
        size="2.4rem"
      />
      <ButtonCircleIcon
        v-else
        class="button-cancel"
        icon-name="previous"
        theme="neutral"
        size="x-small"
        icon-size="1rem"
        stroke="n"
        button-role="annuler"
        variant="outlined"
        @click="handleBack"
      />
      {{ currentBox.title }}
    </template>

    <DialogSettingsPreferencesBox :box="currentBox" />
  </DialogTemplate>
</template>
<script lang="ts"></script>
<script lang="ts" setup>
import DialogSettings from '~/components/dialog/settings/DialogSettings.vue';
import DialogError from '~/components/dialog/error/DialogError.vue';

interface Props {
  fromUserDialog?: boolean;
  defaultPaths?: string[];
}

const props = defineProps({
  fromUserDialog: { type: Boolean, default: false },
  defaultPaths: { default: () => ['root'] }
});

const settingsPreferences = useSettingsPreferences();

const { t } = useI18n();

provide(INJECT_SETTINGS_PREFERENCES, settingsPreferences);

const paths = ref<string[]>(props.defaultPaths);

provide(Constant.PARAMETERS_PATHS, paths);

const dialog = useDialogStore();

const route = useRoute();

watch(
  () => route.path,
  () => {
    dialog.close();
  }
);

const { result, error } = await useApiFetchCache(URI.MemberPreferences);

if (error) {
  dialog.open(DialogError, {
    level: DialogLevel.Info,
    iconHeader: 'warning',
    label: t('error.request'),
  });
}

const { boxes, preferences } = result;

const populate = (box, depth: number = 0) => {
  const children = box.children
    .map((child) => {
      if (child.type === 'box') {
        const b = boxes.find((box) => box.name === child.name);
        if (b) {
          const childBox = populate(b, depth + 1);
          return {
            ...child,
            ...childBox,
          };
        }
        return null;
      } else {
        const p = preferences.find((pref) => pref.name === child.name);
        if (p) {
          return {
            ...child,
            ...preferences.find((pref) => pref.name === child.name),
          };
        }
        return null;
      }
    })
    .filter((item) => item !== null);
  return {
    ...box,
    children,
  };
};

const currentPath = computed(() => paths.value[paths.value.length - 1]);

const currentBox = computed(() => {
  const box = boxes.find((box) => box.name === currentPath.value);
  return populate(box);
});

const handleBack = () => {
  if (paths.value.length > 1) {
    paths.value.pop();
  } else if (props.fromUserDialog) {
    dialog.open(DialogSettings, {
      level: DialogLevel.Settings,
      position: 'top-right',
      fromUserDialog: true,
    });
  } else {
    dialog.close();
  }
};
</script>
<style lang="scss" scoped>
.dialog {
  padding: 1rem;
  &:deep(.content) {
    gap: 2.6rem;
  }
}
.header-infos {
  display: grid;
  grid-template-columns: 15% auto;
  gap: 2rem;
  @include above('l') {
    width: 40rem;
  }
  .avatar {
    align-self: center;
    overflow: hidden;
    img {
      object-fit: contain;
      &.solid {
        width: 100%;
        height: auto;
        border-radius: 50%;
        background-color: var(--color_neutral_50);
      }
    }
  }
}
</style>
